import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { getWholeYearsBetweenDates, reconcileForecastNetDebtValues } from './DealThesisHelpers';
import { cloneToMutableData } from '@app/shared/helpers';
import { DEAL_THESIS_TABLE_HEADER_ADDITIONAL_COL_COUNT } from '@app/shared/constants/table-config';
import {
  Pwerm2InputYearDebtInstrumentCalculationResultsDto,
  Pwerm2InputYearDebtInstrumentCalculationResultsEventsDto,
  Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto,
} from '@app/shared/models/contracts/pwerm2-input-calculation-results-dto';

export interface HistoricEventData {
  ordinaryInstruments: Dictionary<Pwerm2InputYearOrdinaryEquityInstrumentCalculationResultsDto>;
  shareholderDebtInstruments: Dictionary<Pwerm2InputYearDebtInstrumentCalculationResultsDto>;
  shareholderDebtInstrumentsEvents: Dictionary<Pwerm2InputYearDebtInstrumentCalculationResultsEventsDto>;
  shareholderDebtSummary: Dictionary<Dictionary<number>>;
  shareholderDebtTotalByType: Dictionary<number>;
  totalNetDebt: number;
  historicEventsYear: string;
}

function filterHistoricEventData(
  fullHistoricYearData: HistoricEventData[],
  valuationDate: string,
  pwermHistoricForecastsLength: number
): HistoricEventData[] {
  const firstHistoricYearWithAnEvent = fullHistoricYearData.find(
    (historicYear) =>
      Object.values(historicYear.shareholderDebtInstrumentsEvents ?? {}).some(
        (event) => event?.periodByEvent.length > 1 || debtInstrumentEventYearHasInterest(event)
      ) ||
      Object.values(historicYear.ordinaryInstruments ?? {}).some(
        (event) => event?.periodByEvent.length > 0
      )
  );

  let cutoffYear = firstHistoricYearWithAnEvent?.historicEventsYear ?? valuationDate;
  const numberOfYears = getWholeYearsBetweenDates(valuationDate, cutoffYear);
  if (numberOfYears && numberOfYears < pwermHistoricForecastsLength) {
    cutoffYear = addYearsToDate(valuationDate, -pwermHistoricForecastsLength);
  }

  return fullHistoricYearData.filter((x) => x.historicEventsYear >= cutoffYear);
}

const debtInstrumentEventYearHasInterest = (
  eventYear: Pwerm2InputYearDebtInstrumentCalculationResultsEventsDto
): boolean =>
  Boolean(eventYear?.periodTotal.accruedInterest) ||
  Boolean(eventYear?.periodTotal.cashPayInterest);

function addYearsToDate(dateString: string, years: number): string {
  const date = new Date(dateString);
  date.setFullYear(date.getFullYear() + years);
  return date.toISOString().slice(0, 10);
}

const useDealThesisData = (caseId: string | undefined) => {
  const project = useAppSelector((state) => state.project.projectDraft);
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedInputs);
  const caseResults = results.cases[caseId!];
  const pwermInputHistoricForecasts = cloneToMutableData(
    project.pwermInput.historicForecasts
  ).reverse();
  const pwermCaseData = project.pwermInput.cases.find((caseItem) => caseItem.caseId === caseId);
  const getPwermForecastCount = () => pwermCaseData?.forecasts.length ?? 0;
  const pwermCaseDataForecasts = pwermCaseData
    ? reconcileForecastNetDebtValues(
        pwermCaseData.forecasts.slice(0, getPwermForecastCount()),
        project.equityBridge.netDebtItems
      )
    : undefined;
  const showHistoricDataColumns = useAppSelector(
    (state) => state.uiValues.userSelections.dealThesis.showHistoricDataColumns
  );

  const historicDataForecastYearsResults = Object.entries(
    caseResults.historicDates.historicForecastYears ?? {}
  )
    .map(([historicYears, value]) => ({
      historicYears,
      ...value,
    }))
    .slice(0, pwermInputHistoricForecasts.length);

  const caseResultsForecastDates = Object.entries(caseResults.forecastDates)
    .map(([forecastId, value]) => ({
      forecastId,
      ...value,
    }))
    .slice(0, getPwermForecastCount());

  const historicEventsData = Object.entries(caseResults.historicDates.historicEventYears ?? {}).map(
    ([historicEventsYear, value]) => ({
      historicEventsYear,
      ...value,
    })
  );

  const filteredHistoricEventData = filterHistoricEventData(
    historicEventsData,
    project.valuationDate,
    project.pwermInput.historicForecasts.length
  );

  const emptyColumnsBeforeHistoricDataForecastYearsResults =
    filteredHistoricEventData.length - historicDataForecastYearsResults.length;

  const emptyColumnsBeforeProgramableHistoricEventYears =
    historicDataForecastYearsResults.length - filteredHistoricEventData.length;

  const getMaxColspan = () =>
    getPwermForecastCount() +
    filteredHistoricEventData.length +
    pwermInputHistoricForecasts.length +
    DEAL_THESIS_TABLE_HEADER_ADDITIONAL_COL_COUNT;

  return {
    showHistoricDataColumns,
    caseResultsForecastDates,
    pwermCaseData,
    pwermCaseDataForecasts,
    pwermInputHistoricForecasts,
    historicDataForecastYearsResults,
    getMaxColspan,
    getPwermForecastCount,
    filteredHistoricEventData,
    emptyColumnsBeforeHistoricDataForecastYearsResults,
    emptyColumnsBeforeProgramableHistoricEventYears,
  };
};

export default useDealThesisData;
